import type { AppDispatch } from '../index';
import { getAffiliateLinks } from '../../lib/api/affiliate-signed-url-client';
import { getPublicWebsiteConfig } from '../../lib/api/config';
import {
  affiliateHydrationFailed,
  affiliateHydrationRequested,
  gameDetailAffiliateLinksHydrated,
} from '../slices/games-slice';

/**
 * Affiliate thunks (task 6.5, public-website spec).
 *
 * Implements the SPA half of the design §8.2 Game-page on-demand
 * affiliate hydration: the `<GamePage>` component (task 6.9) — or
 * the `loadGameDetail` thunk in `./games-thunks.ts` — dispatches
 * `hydrateGameAffiliateLinks` whenever a `GetGameByIdV1` response
 * carries an empty `affiliateLinks` array. The thunk performs the
 * two-hop signed-URL transport via
 * `lib/api/affiliate-signed-url-client.ts` and dispatches:
 *
 *  - `affiliateHydrationRequested` on entry (slice flips
 *    `affiliateHydrationStatus` to `'loading'`),
 *  - `gameDetailAffiliateLinksHydrated` on success (slice merges
 *    the fetched `links` into the cached `GameDetail` and flips
 *    `affiliateHydrationStatus` to `'succeeded'`),
 *  - `affiliateHydrationFailed` on rejection (slice records the
 *    error message and flips `affiliateHydrationStatus` to
 *    `'failed'`).
 *
 * Empty `links: []` is the normal "no matches" outcome per R12.6
 * — the thunk still dispatches the success action so the slice's
 * `affiliateHydrationStatus` flips to `'succeeded'`; the merge
 * reducer leaves any prior list intact rather than overwriting it
 * with the empty array.
 *
 * Function-length budget: every export below stays under 20 lines
 * per Requirement 3.7.
 */

/** Input shape for `hydrateGameAffiliateLinks`. */
export interface HydrateGameAffiliateLinksInput {
  /** Game id used as the key in `state.games.gameDetailById`. */
  readonly id: string;
  /** Canonical title from the cached `GameDetail`; passed to the mint hop. */
  readonly gameTitle: string;
}

/**
 * Fetch affiliate links via the design §8.2 signed-URL transport
 * and merge them into the cached Game-detail record.
 *
 * @param input The game id + canonical title.
 * @returns A thunk that resolves once the matching slice action
 *   has been dispatched (`gameDetailAffiliateLinksHydrated` on
 *   success, `affiliateHydrationFailed` on error).
 */
export function hydrateGameAffiliateLinks(input: HydrateGameAffiliateLinksInput) {
  return async (dispatch: AppDispatch): Promise<void> => {
    dispatch(affiliateHydrationRequested(input.id));
    try {
      const config = getPublicWebsiteConfig();
      const response = await getAffiliateLinks(
        { gameTitle: input.gameTitle },
        { baseUrl: config.publicApiInvokeUrl, apiKey: config.publicApiKey },
      );
      dispatch(
        gameDetailAffiliateLinksHydrated({
          id: input.id,
          links: response.links,
        }),
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(affiliateHydrationFailed({ id: input.id, error: message }));
    }
  };
}
